/**
 * A skill edit's unsaved draft (ADR-216 Amendment 1), kept in the browser's
 * durable edit buffer (ADR-033) so a refresh or a closed tab loses nothing.
 * The draft never reaches SystemDox until it is published as a version.
 */
import { editBufferService } from '@/services/editBufferService';
import type { SkillFileText } from './skillFiles';

export interface SkillDraft {
  baseVersion: number;
  files: SkillFileText[];
  note: string;
  updatedAt: number;
}

/** One buffer per skill, beside the repo documents' buffers. */
const REPO = 'systemdox-skills';
const key = (name: string) => `${REPO}::${name}::`;

export async function loadDraft(name: string): Promise<SkillDraft | null> {
  const buffer = await editBufferService.get(key(name));
  if (!buffer) return null;
  try {
    const { files, note } = JSON.parse(buffer.content) as { files: SkillFileText[]; note?: string };
    if (!Array.isArray(files)) return null;
    return { baseVersion: Number(buffer.baseSha), files, note: note ?? '', updatedAt: buffer.updatedAt };
  } catch {
    return null; // a buffer this code cannot read is not a draft worth offering
  }
}

export async function saveDraft(name: string, draft: Omit<SkillDraft, 'updatedAt'>): Promise<void> {
  await editBufferService.save({
    key: key(name),
    repoFullName: REPO,
    path: name,
    branch: '',
    content: JSON.stringify({ files: draft.files, note: draft.note }),
    baseSha: String(draft.baseVersion),
    updatedAt: Date.now(),
  });
}

export async function discardDraft(name: string): Promise<void> {
  await editBufferService.remove(key(name));
}

/** Whether a draft holds anything its base version does not. */
export function draftDiffers(draft: SkillDraft, base: readonly SkillFileText[]): boolean {
  if (draft.files.length !== base.length) return true;
  const was = new Map(base.map((f) => [f.path, f.content]));
  return draft.files.some((f) => was.get(f.path) !== f.content);
}
